import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  inject,
  input,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Content, Editor, type EditorEvents } from '@tiptap/core';
import { filter, tap } from 'rxjs';
import { migrateDoubleDollarMath } from './util';

export enum TiptapEditorOutputFormat {
  Html = 'html',
  Json = 'json',
}

/**
 * Patched version of the ngx-tiptap editor directive
 */
@Directive({
  selector: 'tiptap[editor], [tiptap][editor], tiptap-editor[editor], [tiptapEditor][editor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TiptapEditorFormControlDirective),
      multi: true,
    },
  ],
})
export class TiptapEditorFormControlDirective<T extends TiptapEditorOutputFormat>
  implements AfterViewInit, ControlValueAccessor
{
  constructor() {
    toObservable(this.editor)
      .pipe(
        filter(Boolean),
        filter(() => !this.isInitialized),
        tap((editor) => {
          this.isInitialized = true;
          this.initializeEditor(editor);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
  protected elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  protected renderer = inject(Renderer2);
  protected changeDetectorRef = inject(ChangeDetectorRef);

  readonly editor = input<Editor | null>(null);
  readonly outputFormat = input.required<T>();

  private isInitialized = false;
  private pendingValue: Content | null = null;
  private isFirstWrite = true;

  protected onChange: (value: Content) => void = () => {
    /** */
  };
  protected onTouched: () => void = () => {
    /** */
  };

  // Writes a new value to the element.
  // This methods is called when programmatic changes from model to view are requested.
  writeValue(value: Content): void {
    const editor = this.editor();
    const isEmptyValue = !value || (typeof value === 'object' && Object.keys(value).length === 0);

    if (isEmptyValue) {
      editor?.chain().setMeta('addToHistory', false).clearContent().run();
      return;
    }

    if (!editor) {
      // Store the value to apply later when editor is ready
      this.pendingValue = value;
      return;
    }

    // Make sure the editor is ready before setting content
    // This prevents issues when writeValue is called before initialization
    const setContent = () => {
      if (this.isFirstWrite) {
        // Prevent initial content from being added to history to avoid users undoing to empty state
        editor.chain().setMeta('addToHistory', false).setContent(value, { emitUpdate: false }).run();
        this.isFirstWrite = false;
      } else {
        editor.chain().setContent(value, { emitUpdate: false }).run();
      }
    };

    if (editor && editor.options.element) {
      setContent();
    } else {
      // If editor is not ready yet, defer the content setting
      requestAnimationFrame(setContent);
    }

    migrateDoubleDollarMath(editor);
  }

  // Registers a callback function that is called when the control's value changes in the UI.
  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  // Registers a callback function that is called by the forms API on initialization to update the form model on blur.
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Called by the forms api to enable or disable the element
  setDisabledState(isDisabled: boolean): void {
    const editor = this.editor();
    if (!editor) {
      return;
    }
    editor.setEditable(!isDisabled);
    this.renderer.setProperty(this.elRef.nativeElement, 'disabled', isDisabled);
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  private initializeEditor(editor: Editor): void {
    // Register event handlers
    editor.on('blur', () => {
      this.onTouched();
      this.changeDetectorRef.markForCheck();
    });

    editor.on('update', this.handleChange);

    editor.on('selectionUpdate', () => {
      this.changeDetectorRef.markForCheck();
    });

    // Apply any pending value that was set before editor was ready
    if (this.pendingValue) {
      // Prevent initial content from being added to history to avoid users undoing to empty state
      editor.chain().setMeta('addToHistory', false).setContent(this.pendingValue, { emitUpdate: false }).run();
      migrateDoubleDollarMath(editor);
      this.pendingValue = null;
      this.isFirstWrite = false;
    }
  }

  protected handleChange = ({ editor, transaction }: EditorEvents['transaction']): void => {
    if (!transaction.docChanged || transaction.getMeta('addToHistory') === false || !editor.isEditable) {
      return;
    }

    this.changeDetectorRef.markForCheck();

    const format = this.outputFormat();
    if (format === 'html') {
      this.onChange(editor.getHTML());
      return;
    }

    this.onChange(editor.getJSON());
  };
}
